import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { useSelector } from "react-redux";
import { RootState } from "store";
import ChatLayout from "Layouts/ChatLayout/ChatLayout";
import { EntityChatRoom } from "types/chat";
import InitialAvatar from "react-avatar";
import Avatar from "react-avatar";
import { useFirebase } from "contexts/FirebaseAuthContext";
import CollaborationChatMessages, {
    useCollaborationChatMessages,
} from "Components/campaigns/collaborations/chat/CollaborationChatMessages";

export default function CollaborationChat() {
    const { uuid: activeRoomId } = useParams();

    const agencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const currentEntityId = agencyId;
    const { firebaseUser, db } = useFirebase();
    const [entityChatRoom, setEntityChatRoom] = useState<EntityChatRoom | null>(null);
    const [listLoaded, setListLoaded] = useState(false);

    const messagesState = useCollaborationChatMessages({ entityChatRoom, currentEntityId });

    const fetchEntityChatRoom = async () => {
        if (!firebaseUser || !db || !activeRoomId || !currentEntityId) return;
        try {
            const roomDoc = await getDoc(
                doc(db, "entityChatRooms", currentEntityId, "rooms", activeRoomId)
            );
            if (roomDoc.exists()) {
                setEntityChatRoom({ ...roomDoc.data(), uuid: activeRoomId } as EntityChatRoom);
            }
        } catch (error) {
            console.error("Failed to fetch entity chat room:", error);
        }
    };

    useEffect(() => {
        if (listLoaded && !entityChatRoom && firebaseUser && db && activeRoomId && currentEntityId) {
            fetchEntityChatRoom();
        }
    }, [listLoaded, entityChatRoom, firebaseUser, db, activeRoomId, currentEntityId]);

    return (
        <ChatLayout setEntityChatRoom={setEntityChatRoom} setListLoaded={setListLoaded}>
            {!activeRoomId ? (
                <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                    <div className="length-zero m-0">
                        <h3>No Chat Selected!</h3>
                    </div>
                </div>
            ) : (
                <div className="chat-content d-lg-flex">
                    <div className="w-100 overflow-hidden position-relative">
                        <div className="position-relative">
                            <div className="p-3 user-chat-topbar">
                                <div className="d-flex justify-content-between gap-2 align-items-center">
                                    <div className="d-flex align-items-center w-50">
                                        <div className="flex-shrink-0 d-block d-lg-none me-1">
                                            <Link to={{ pathname: "/chat" }} className="user-chat-remove fs-18 p-1">
                                                <i className="ri-arrow-left-s-line align-bottom"></i>
                                            </Link>
                                        </div>
                                        {entityChatRoom && (
                                            <div className="flex-grow-1 overflow-hidden">
                                                <div className="d-flex align-items-center">
                                                    <div className="flex-shrink-0 chat-user-img online user-own-img align-self-center me-2 ms-0">
                                                        {entityChatRoom.social_account?.profile_picture_url ? (
                                                            <img
                                                                src={entityChatRoom.social_account?.profile_picture_url}
                                                                className="rounded-circle avatar-xs"
                                                                alt=""
                                                            />
                                                        ) : (
                                                            <InitialAvatar
                                                                name={entityChatRoom.influencer?.name ?? entityChatRoom.social_account?.name ?? `@${entityChatRoom.social_account?.handle}`}
                                                                size="32"
                                                                round
                                                                className="rounded-circle avatar-xs"
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="overflow-hidden">
                                                        <h5 className="text-truncate w-100 mb-0 fs-16">
                                                            <span className="text-reset username">
                                                                {entityChatRoom.influencer?.name ?? entityChatRoom.social_account?.name ?? `@${entityChatRoom.social_account?.handle}`}
                                                                <p className="text-muted fs-12 mb-0">
                                                                    {entityChatRoom.campaign.name}
                                                                </p>
                                                            </span>
                                                        </h5>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {entityChatRoom?.campaign && (
                                        <Link
                                            to={{
                                                pathname: `/collaboration/campaign/${entityChatRoom.campaign.uuid}/collaboration/${entityChatRoom.collaboration?.uuid ?? ''}`,
                                            }}
                                            className="text-primary open-collab-link flex-shrink-0"
                                        >
                                            <svg width="20" height="20" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" clipRule="evenodd" d="M8.57411 1.56251C8.6113 1.56252 8.64899 1.56252 8.68721 1.56252L8.73344 1.56252C9.17273 1.56249 9.45541 1.56247 9.73177 1.58831C10.8591 1.69373 11.9315 2.11913 12.8233 2.81252H20.625C20.6495 2.81252 20.6736 2.81252 20.6973 2.81251C21.2082 2.81243 21.5561 2.81237 21.8619 2.85262C23.9653 3.12955 25.6205 4.78474 25.8974 6.88818C25.9079 6.96746 25.9156 7.04958 25.9213 7.13664C26.2682 7.29474 26.5893 7.49918 26.8847 7.76481C27.0076 7.8754 27.1246 7.99242 27.2352 8.11538C27.8956 8.84957 28.1777 9.74324 28.31 10.795C28.4376 11.8099 28.4375 13.0926 28.4375 14.6838V17.5705C28.4375 19.8677 28.4376 21.6873 28.2461 23.1113C28.0491 24.5768 27.6339 25.763 26.6985 26.6985C25.763 27.6339 24.5768 28.0491 23.1113 28.2461C21.6873 28.4376 19.8677 28.4375 17.5705 28.4375H12.4295C10.1323 28.4375 8.31276 28.4376 6.88874 28.2461C5.42321 28.0491 4.23703 27.6339 3.30157 26.6985C2.36612 25.763 1.95097 24.5768 1.75394 23.1113C1.56248 21.6873 1.5625 19.8677 1.56252 17.5705L1.56252 8.6872C1.56252 8.64899 1.56252 8.6113 1.56251 8.57411C1.56237 7.57922 1.56228 6.94606 1.66546 6.39919C2.11863 3.99735 3.99735 2.11863 6.39919 1.66546C6.94606 1.56228 7.57922 1.56237 8.57411 1.56251ZM23.9342 6.6596C23.6119 5.62863 22.7166 4.85632 21.6171 4.71158C21.4497 4.68954 21.2353 4.68752 20.625 4.68752H14.7642C15.5678 5.48635 15.9324 5.81998 16.346 6.05006C16.6185 6.20168 16.9075 6.32142 17.2075 6.4069C17.7241 6.55417 18.2905 6.56252 19.7856 6.56252L20.3163 6.56252C21.7691 6.5625 22.9648 6.56248 23.9342 6.6596ZM8.68721 3.43752C7.5414 3.43752 7.09841 3.44162 6.74682 3.50795C5.10346 3.81801 3.81801 5.10346 3.50795 6.74682C3.44162 7.09841 3.43752 7.5414 3.43752 8.6872V17.5C3.43752 19.8835 3.43951 21.5769 3.61222 22.8615C3.7813 24.1191 4.09839 24.8436 4.6274 25.3726C5.15641 25.9017 5.88097 26.2187 7.13858 26.3878C8.42316 26.5605 10.1165 26.5625 12.5 26.5625H17.5C19.8835 26.5625 21.5769 26.5605 22.8615 26.3878C24.1191 26.2187 24.8436 25.9017 25.3726 25.3726C25.9017 24.8436 26.2187 24.1191 26.3878 22.8615C26.5605 21.5769 26.5625 19.8835 26.5625 17.5V14.7474C26.5625 13.0776 26.5608 11.9131 26.4496 11.0289C26.3414 10.1681 26.1426 9.70445 25.8411 9.36924C25.7748 9.29546 25.7046 9.22525 25.6308 9.15889C25.2956 8.8574 24.8319 8.65865 23.9711 8.55043C23.0869 8.43926 21.9225 8.43752 20.2526 8.43752H19.7856C19.739 8.43752 19.6931 8.43753 19.6477 8.43754C18.3378 8.43781 17.493 8.43798 16.6935 8.21008C16.2551 8.08514 15.8327 7.91014 15.4344 7.68854C14.7079 7.28434 14.1106 6.68687 13.1846 5.76041C13.1525 5.72831 13.12 5.69582 13.0871 5.66293L12.3993 4.97512C12.0452 4.62102 11.9097 4.48689 11.7716 4.37242C11.1425 3.85092 10.3708 3.53125 9.5572 3.45517C9.37862 3.43847 9.18798 3.43752 8.68721 3.43752ZM15.3125 12.5C15.3125 11.9823 15.7323 11.5625 16.25 11.5625H22.5C23.0178 11.5625 23.4375 11.9823 23.4375 12.5C23.4375 13.0178 23.0178 13.4375 22.5 13.4375H16.25C15.7323 13.4375 15.3125 13.0178 15.3125 12.5Z" fill="#007AFF" />
                                            </svg>
                                            <span className="open-collab-link">Open Collab</span>
                                        </Link>
                                    )}
                                </div>
                            </div>

                            <div className="position-relative" id="users-chat">
                                <div className="chat-conversation chat-messages-panel" id="chat-conversation">
                                    {entityChatRoom ? (
                                        <CollaborationChatMessages
                                            entityChatRoom={entityChatRoom}
                                            messagesState={messagesState}
                                            dayLabelVariant="class"
                                        />
                                    ) : (
                                        <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                                            <div className="length-zero m-0">
                                                <h3>Loading chat...</h3>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </ChatLayout>
    );
}
